/* The community card of the card library, also dealt by the character builder. */

import { CSSProperties } from 'react';
import { IterationCcw, IterationCw, X } from 'lucide-react';
import { Community, CommunityFeature } from '../content/communities';
import { sourceLabel } from '../content/sources';
import heritageArt from './heritage-art.svg';
import './cards.css';

const DOMAIN_COLOR = '#4db8c8';

// ── Shared card-sheet renderer ────────────────────────────────────────
export function CommunitySheet({ community, preview = false, flipped = false, onFlip, onClose }: {
  community: Community;
  preview?: boolean;
  flipped?: boolean;
  onFlip?: () => void;
  onClose?: () => void;
}) {
  const source = sourceLabel(community.source);
  const style = { '--domain-color': DOMAIN_COLOR } as CSSProperties;
  const bg = (
    <div className="card-sheet__bg" aria-hidden="true">
      <img src={heritageArt} className="card-sheet__icon" alt="" />
    </div>
  );
  const footerBase = (page: string) => (
    <div className="card-sheet__footer">
      <span>{source}</span>
      <span className="card-sheet__footer-dot">·</span>
      <span>dhtools.net</span>
      <span className="card-sheet__footer-dot">·</span>
      <span>{page}</span>
    </div>
  );

  if (preview) {
    return (
      <div className="portrait-card" style={style}>
        {bg}
        <div className="card-sheet__type">Community</div>
        <h2 className="card-sheet__name">{community.name}</h2>
        <div className="card-sheet__rule" aria-hidden="true" />
        <div className="card-sheet__body">
          {community.cardDescription && (
            <p className="card-text-para">{community.cardDescription}</p>
          )}
          {(community.features || []).map((f: CommunityFeature, i: number) => (
            <div key={i} className="card-feature">
              <span className="card-feature__name">{f.name}</span>
              <span className="card-feature__text">{f.text}</span>
            </div>
          ))}
        </div>
        {footerBase('1/2')}
      </div>
    );
  }

  return (
    <div className={`portrait-card__flipper${flipped ? ' portrait-card__flipper--flipped' : ''}`}>
      {/* Front face */}
      <div className="portrait-card portrait-card__front" style={style}>
        {bg}
        <div className="card-sheet__type">Community</div>
        <h2 className="card-sheet__name">{community.name}</h2>
        <div className="card-sheet__rule" aria-hidden="true" />
        <div className="card-sheet__body">
          {community.cardDescription && (
            <p className="card-text-para">{community.cardDescription}</p>
          )}
          {(community.features || []).map((f: CommunityFeature, i: number) => (
            <div key={i} className="card-feature-full">
              <span className="card-feature__name">{f.name}</span>
              <span className="card-feature__text">{f.text}</span>
            </div>
          ))}
        </div>
        {onFlip && <button className="portrait-card__flip-btn" onClick={(e) => { e.stopPropagation(); onFlip(); }} aria-label="See lore"><IterationCw size={15} /><span className="portrait-card__flip-label">See lore</span></button>}
        {onClose && <button className="card-sheet__close" onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Close"><X size={14} /></button>}
        {footerBase('1/2')}
      </div>
      {/* Back face — lore */}
      <div className="portrait-card portrait-card__back" style={style}>
        {bg}
        <div className="card-sheet__type">Lore</div>
        <h2 className="card-sheet__name">{community.name}</h2>
        <div className="card-sheet__rule" aria-hidden="true" />
        <div className="card-sheet__body">
          {(community.description || []).map((d, i) => (
            <p key={i} className="card-text-para">{d}</p>
          ))}
        </div>
        {onFlip && <button className="portrait-card__flip-btn portrait-card__flip-btn--back" onClick={(e) => { e.stopPropagation(); onFlip(); }} aria-label="Back to card"><IterationCcw size={15} /><span className="portrait-card__flip-label">Back</span></button>}
        {onClose && <button className="card-sheet__close" onClick={(e) => { e.stopPropagation(); onClose(); }} aria-label="Close"><X size={14} /></button>}
        {footerBase('2/2')}
      </div>
    </div>
  );
}
